/**
 * Build provenance for the running bundle.
 *
 * `__BUILD_COMMIT__` and `__BUILD_TIME__` are inlined by Vite at build time
 * (see `define` in `vite.config.ts`). In CI they come from the commit and clock
 * of the deploy job, so they identify exactly what Cloudflare Pages is serving;
 * on a developer machine they come from the local `git` checkout and the moment
 * the dev server started.
 *
 * Both are read defensively — a bundler or test runner that doesn't apply the
 * `define` leaves the identifier undefined rather than an empty string, and a
 * checkout without git history yields the literal `"unknown"`.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Full commit sha the bundle was built from, or `undefined` if unavailable. */
export const BUILD_COMMIT: string | undefined =
  typeof __BUILD_COMMIT__ === "string" ? __BUILD_COMMIT__ : undefined;

/** ISO timestamp of the build, or `undefined` if unavailable. */
export const BUILD_TIME: string | undefined =
  typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : undefined;

/**
 * Abbreviate a commit sha for display. Returns `null` when there is nothing
 * meaningful to show, so callers can hide the row entirely.
 */
export function formatCommitSha(sha: string | undefined | null): string | null {
  const trimmed = sha?.trim();
  if (!trimmed || trimmed === "unknown") return null;
  return trimmed.slice(0, 7);
}

/**
 * Render how long ago the bundle was built as a compact relative age
 * ("just now", "5m ago", "3h ago", "2d ago", "2mo ago", "2y ago").
 *
 * Returns `null` for a missing or unparseable timestamp. A timestamp in the
 * future — clock skew between the build machine and the browser — is clamped to
 * "just now" rather than rendered as a negative age.
 */
export function formatDeployAge(
  builtAt: string | undefined | null,
  now: Date = new Date()
): string | null {
  if (!builtAt) return null;

  const built = new Date(builtAt);
  if (Number.isNaN(built.getTime())) return null;

  const elapsed = now.getTime() - built.getTime();
  if (elapsed < MINUTE) return "just now";

  const minutes = Math.floor(elapsed / MINUTE);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(elapsed / HOUR);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(elapsed / DAY);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** Absolute build time for the `title` tooltip, in the viewer's locale. */
export function formatDeployTimestamp(
  builtAt: string | undefined | null
): string | null {
  if (!builtAt) return null;

  const built = new Date(builtAt);
  if (Number.isNaN(built.getTime())) return null;

  return built.toLocaleString();
}
